/**
 * Drafts and scheduled sends, as the composer and the rail's two lists see them.
 *
 * A draft is the composer's own copy of a letter that has not gone yet: saved a
 * second after every change, listed under Drafts, and - with a time set - sent
 * by the server at that time. Every call here acts on the signed-in operator's
 * drafts only; a draft is personal.
 */
import { API_BASE, api } from "./api.js";

export type DraftMode = "new" | "reply" | "forward";
export type DraftStatus = "draft" | "scheduled" | "sending" | "failed";

/** What a save sends: the whole content, never a patch. */
export interface DraftContent {
  mode: DraftMode;
  threadId?: string | null;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  bodyHtml: string;
  bodyText: string;
  /** Absent: the mailbox default. `null`: none. A string: that signature. */
  signatureId?: string | null;
  /** Absent: the default sender when it goes. A string: that address. */
  fromAddress?: string;
  attachmentIds: string[];
}

/** One row of the Drafts or Scheduled list. */
export interface DraftListItem {
  id: string;
  mode: DraftMode;
  threadId: string | null;
  to: string[];
  subject: string;
  snippet: string;
  attachmentCount: number;
  scheduledAt: string | null;
  status: DraftStatus;
  /** Why it could not be sent, as a code the catalogue words. */
  error: string | null;
  updatedAt: string;
}

/** A draft opened to be written further. */
export interface DraftDetail extends DraftListItem {
  cc: string[];
  bcc: string[];
  bodyHtml: string;
  signatureSet: boolean;
  signatureId: string | null;
  /** The sender the writer picked. Null: the default. Optional because an API
   *  older than the sender list does not send it. */
  fromAddress?: string | null;
  /** The staged files that can still be sent, in the order they were added. */
  attachments: { id: string; name: string; size: number }[];
}

/** One mailbox's drafts when `mailbox` is named, every draft the caller has when not. */
export const listDrafts = (kind: "drafts" | "scheduled", mailbox?: string) =>
  api.get<{ rows: DraftListItem[] }>(
    `/admin/crm/mail/drafts?kind=${kind}${mailbox ? `&mailbox=${encodeURIComponent(mailbox)}` : ""}`,
  );

export const getDraft = (id: string) => api.get<DraftDetail>(`/admin/crm/mail/drafts/${id}`);

export const createDraft = (content: DraftContent) =>
  api.post<{ id: string; updatedAt: string }>("/admin/crm/mail/drafts", content);

export const saveDraft = (id: string, content: DraftContent) =>
  api.put<{ id: string; updatedAt: string }>(`/admin/crm/mail/drafts/${id}`, content);

export const deleteDraft = (id: string) => api.del<{ ok: boolean }>(`/admin/crm/mail/drafts/${id}`);

export const scheduleDraft = (id: string, at: string) =>
  api.post<DraftListItem>(`/admin/crm/mail/drafts/${id}/schedule`, { at });

export const unscheduleDraft = (id: string) =>
  api.post<DraftListItem>(`/admin/crm/mail/drafts/${id}/unschedule`, {});

export const sendDraftNow = (id: string) =>
  api.post<{ ok: true; threadId: string; transport: string }>(`/admin/crm/mail/drafts/${id}/send`, {});

/**
 * The last save, sent as the page goes away.
 *
 * `keepalive` is what lets a request outlive the tab that made it - an ordinary
 * fetch is cancelled with the page, and "I closed the browser and my letter
 * was gone" is exactly what drafts are for. Nothing reads the answer: there is
 * nobody left to show it to. A draft that was never saved is created here,
 * which is why the path depends on whether there is an id yet.
 */
export function saveDraftOnExit(id: string | null, content: DraftContent): void {
  try {
    void fetch(`${API_BASE}/admin/crm/mail/drafts${id ? `/${id}` : ""}`, {
      method: id ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(content),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // A browser that refuses the request at unload has already decided.
  }
}
